// @owners { team: patients }
import { Toast, ToastContext } from '@alto/design-system';
import { getAllergensForInput } from '@alto/features';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
// eslint-disable-next-line import/no-deprecated
import { updateOnboardingUser } from '~shared/actions/onboarding';
// eslint-disable-next-line import/no-deprecated
import { fetchUsers } from '~shared/actions/users';
import { NO_KNOWN_ALLERGIES, NO_KNOWN_MEDICAL_CONDITIONS, getVerifyFailureErrorMessage } from '~shared/constants';
import {
  ONBOARD_STATE_MEDICAL_INFO,
  PHI_AUTHORIZATION,
} from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { ROUTE_TO_URL, routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import { getCurrentUser } from '~shared/features/users/selectors/getCurrentUser';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import { getErrorMessage } from '~shared/helpers/helper';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useNavigationContext } from '~web/contexts';
import {
  type MedicalInfoValues,
  OnboardingMedicalInfoPage,
} from '~web/features/onboarding/components/redesign/OnboardingMedicalInfoPage';

const OnboardingMedicalInfo = () => {
  const dispatch = useDispatchShared();
  const { trackEvent, trackPageView } = useAnalytics();
  const { addToast } = useContext(ToastContext);
  const { navigate } = useNavigation();
  const { previous: previousUrl } = useNavigationContext();
  const origin = previousUrl ? routeFromPath(previousUrl) : '';
  const updateOnboardingUserError = useSelectorShared((state) => state.ui.errors.updateOnboardingUserError);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const user = useSelectorShared(getCurrentUser);

  const methods = useForm<MedicalInfoValues>({
    defaultValues: {
      allergens: getAllergensForInput(user),
      medical_conditions: user?.medical_conditions || NO_KNOWN_MEDICAL_CONDITIONS,
    },
  });

  const onSave = async (userUpdate: MedicalInfoValues & { allergies?: string }) => {
    delete userUpdate.hasAllergens;
    trackEvent({
      event: EVENTS.MED_INFO_SUBMITTED,
      params: {
        origin: ORIGIN_NAMES.ONBOARDING,
        is_progyny_rx_view: showProgyny,
      },
    });

    if (userUpdate.allergens.length === 0) {
      userUpdate.allergens = [NO_KNOWN_ALLERGIES];
    }

    const success = await dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({ ...userUpdate }),
    );
    if (success) {
      // eslint-disable-next-line import/no-deprecated
      await dispatch(fetchUsers());
      // not using the nextRoute helper because we want to go to the next route based on the user's onboarding state
      // since the update is async, we wont get the latest nextRoute from the selector here until the next render
      navigate(ROUTE_TO_URL[PHI_AUTHORIZATION]);
    } else {
      addToast(
        <Toast variant="error">{getErrorMessage(updateOnboardingUserError) || getVerifyFailureErrorMessage()}</Toast>,
      );
    }
  };

  useEffect(() => {
    dispatch(
      // eslint-disable-next-line import/no-deprecated
      updateOnboardingUser({
        onboard_state: ONBOARD_STATE_MEDICAL_INFO,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    trackPageView({
      event: EVENTS.ONBOARDING__MEDICAL_INFO_VIEWED,
      params: {
        origin,
        is_progyny_rx_view: showProgyny,
      },
    });
  }, [origin, showProgyny, trackPageView]);

  return (
    <FormProvider {...methods}>
      <OnboardingMedicalInfoPage onSave={onSave} />
    </FormProvider>
  );
};

export default OnboardingMedicalInfo;
